// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator, pageTitlesPrintStyle } from "@bg/App/SharedStyles/GeneralStyles";
import ShareEntityButton from "@bg/Overrides/Components/ShareEntityButton";
import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: LinkPresentationProps;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: InjectableCss;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: InjectableCss;
    printButton?: LinkPresentationProps;
    buttonGridItem?: InjectableCss;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: linkIconStyleGenerator({
        icon: "Mail",
        textSize: "16px",
        iconSize: 18,
    }),
    headerGridContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    printButton: linkIconStyleGenerator({
        icon: "Print",
        textSize: "16px",
        iconSize: 16,
        wrapperCss: css`
            margin-right: 20px;
        `,
    }),
    buttonGridItem: {
        css: css`
            width: 100%;
            justify-content: flex-end;
            align-items: flex-end;
            display: flex;
        `,
    },
    titleGridItem: {
        css: css`
            width: 100%;
        `,
    },
    title: {
        variant: "h1",
        as: "h1",
        css: css`
            text-transform: uppercase;
            ${pageTitlesPrintStyle()};
            @media print {
                min-width: 500px;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<WidgetProps> = ({ fields }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const printLabel = translate("Print");

    const closeHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.headerGridContainer}>
            <StyledWrapper {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice #")}${invoice.invoiceNumber}`}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu hasChildPortal {...styles.narrowOverflowMenu}>
                        <Clickable
                            aria-label={printLabel}
                            {...styles.printClickable}
                            onClick={openPrintDialog}
                            data-test-selector="invoiceDetails_print"
                        >
                            {printLabel}
                        </Clickable>
                        <Clickable onClick={() => setModalIsOpen(true)} data-test-selector="shareEntity_email">
                            {translate("Email")}
                        </Clickable>
                    </OverflowMenu>
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <Link
                        aria-label={translate("Print invoice")}
                        {...styles.printButton}
                        onClick={openPrintDialog}
                        data-test-selector="invoiceDetails_print"
                    >
                        {printLabel}
                    </Link>
                    <Link
                        {...styles.shareEntityButtonStyles}
                        data-test-selector="shareEntity_email"
                        onClick={() => setModalIsOpen(true)}
                    >
                        {translate("Email")}
                    </Link>
                </Hidden>
            </StyledWrapper>
            <ShareEntityButton
                entityId={invoice.invoiceNumber}
                entityName="Invoice"
                isModalOpen={modalIsOpen}
                setIsModalOpen={setModalIsOpen}
                handleClose={closeHandler}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailHeader,
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
